import { DocumentProcessingJob } from '@/models/DocumentProcessingJob';
import { Property, UserProperty } from '@/models/Property';
import { apiClient, isApiError } from '@/services/api';
import { AdminProperty } from '@/services/api/admin';
import { logger } from '@/utils/logger/loggerInstance';

/**
 * Fetches one page of properties in the system (Admin and Advisor only).
 * Buyers must use getMyProperties() instead.
 *
 * Paginated server-side (see packages/functions/api/properties/getAllProperties.ts)
 * to bound the per-request enrichment work — pass `cursor` (from a prior
 * response's `nextCursor`) to fetch the next page. Callers that need the
 * full list (most of them, today — see useAdminProperties) page through it
 * via useInfiniteQuery rather than doing that by hand here.
 */
export const getAllProperties = async (params?: {
  limit?: number;
  cursor?: string;
  search?: string;
}): Promise<{ properties: AdminProperty[]; nextCursor?: string }> => {
  const url = '/properties/all';

  logger.debug(`GET ${url}`);
  const response = await apiClient.get<{ properties: AdminProperty[]; nextCursor?: string }>(url, {
    params: { limit: params?.limit, cursor: params?.cursor, search: params?.search || undefined },
  });

  return response.data;
};

export const getMyProperties = async () => {
  const url = `/properties/my`;

  logger.debug(`GET ${url}`);
  const response = await apiClient.get<UserProperty[]>(url);

  return response.data;
};

export const startPropertyProcessing = async (requestBody: { documentIds: string[] }) => {
  const url = '/properties/start-processing';

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<DocumentProcessingJob>(url, requestBody);

  return response.data;
};

export const markPropertyAsFavorite = async (propertyId: string) => {
  const url = `/properties/${propertyId}/favorite`;

  logger.debug(`POST ${url}`);
  await apiClient.post<void>(url);
};

export const editProperty = async (propertyId: string, requestBody: { name: string }) => {
  const url = `/properties/${propertyId}`;
  logger.debug(`PUT ${url}`);

  try {
    const response = await apiClient.put<Property>(url, requestBody);
    return response.data;
  } catch (error) {
    if (isApiError<{ message?: string }>(error)) {
      throw new Error(error.response?.data.message ?? 'An error occurred');
    }
    throw error;
  }
};

export const updatePropertyPrice = async (
  propertyId: string,
  price: number,
  source: 'manual' | 'estimated' = 'manual',
) => {
  const url = `/properties/${propertyId}`;
  logger.debug(`PUT ${url} (price)`);

  try {
    const response = await apiClient.put<Property>(url, {
      price,
      priceSource: source,
    });
    return response.data;
  } catch (error) {
    if (isApiError<{ message?: string }>(error)) {
      throw new Error(error.response?.data.message ?? 'Failed to update property price');
    }
    throw error;
  }
};

export const updatePropertyHasHOA = async (propertyId: string, hasHOA: boolean) => {
  const url = `/properties/${propertyId}`;
  logger.debug(`PUT ${url} (hasHOA)`);

  try {
    const response = await apiClient.put<Property>(url, { hasHOA });
    return response.data;
  } catch (error) {
    if (isApiError<{ message?: string }>(error)) {
      throw new Error(error.response?.data.message ?? 'Failed to update HOA status');
    }
    throw error;
  }
};

export const deleteProperty = async (propertyId: string) => {
  const url = `/properties/${propertyId}`;
  logger.debug(`DELETE ${url}`);

  try {
    const response = await apiClient.delete(url);
    return response.data;
  } catch (error) {
    if (isApiError<{ message?: string }>(error)) {
      throw new Error(error.response?.data.message ?? 'An error occurred');
    }
    throw error;
  }
};

export const checkPropertyExists = async (request: {
  address: string;
  unit?: string;
  city: string;
  state: string;
  zipCode: string;
}) => {
  const url = '/properties/check-exists';

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<{ exists: boolean }>(url, request);

  return response.data;
};

export const createProperty = async (request: {
  address: string;
  unit?: string;
  city: string;
  state: string;
  zipCode: string;
  latitude: number;
  longitude: number;
}) => {
  const url = '/properties/create';

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<{ property: Property }>(url, request);

  return response.data;
};

export interface SelfClaimPropertyResponse {
  status: 'claimed' | 'pending_review';
  propertyId?: string;
  requestId?: string;
}

export const selfClaimProperty = async (request: {
  address: string;
  unit?: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
  fullAddress?: string;
  latitude?: number;
  longitude?: number;
}) => {
  const url = '/properties/self-claim';

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<SelfClaimPropertyResponse>(url, request);

  return response.data;
};

export const uploadReportToProperty = async (propertyId: string, documentIds: string[]) => {
  const url = `/properties/${propertyId}/upload-report`;

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<DocumentProcessingJob>(url, { documentIds });

  return response.data;
};
